/**
 * Фильтры витрины. Отбор выполняет сервер — он знает остатки и видимость;
 * здесь только перевод состояния экрана в параметры запроса и подписи
 * активных фильтров, которые покупатель снимает по одному.
 */
import type { Translate } from '../../ui/translate'
import type { CatalogQuery as Query, ProductSort } from './api'

export type RxFilter = 'all' | 'otc' | 'rx'
export type SortKey = 'pop' | 'cheap' | 'exp' | 'name'

export interface Filters {
  rx: RxFilter
  /** Только то, что есть в аптеках. */
  stock: boolean
  /** Границы цены, как их ввёл покупатель. */
  pmin: string
  pmax: string
}

export const EMPTY_FILTERS: Filters = { rx: 'all', stock: false, pmin: '', pmax: '' }

/** Порядок кнопок «условия отпуска»; подписи — `catalog:rxFilter.*`. */
export const RX_FILTERS: RxFilter[] = ['all', 'otc', 'rx']

/** Порядок сортировок; подписи — `catalog:sorts.*`. */
export const SORT_KEYS: SortKey[] = ['pop', 'cheap', 'exp', 'name']

const SORT_PARAM: Record<SortKey, ProductSort> = {
  pop: 'popular',
  cheap: 'price_asc',
  exp: 'price_desc',
  name: 'name',
}

/** Сколько фильтров сейчас сужают выдачу — цифра на кнопке «Фильтры». */
export function filterCount(filters: Filters): number {
  return (
    (filters.rx !== 'all' ? 1 : 0) +
    (filters.stock ? 1 : 0) +
    (filters.pmin ? 1 : 0) +
    (filters.pmax ? 1 : 0)
  )
}

/**
 * Цифра на кнопке «Фильтры»: всё, что в шторке отличается от исходного, —
 * и категория с сортировкой тоже. Иначе выбор в шторке не оставлял следа,
 * и казалось, что фильтры не включились.
 */
export function sheetCount(filters: Filters, category: number, sort: SortKey): number {
  return filterCount(filters) + (category ? 1 : 0) + (sort !== 'pop' ? 1 : 0)
}

export interface CatalogState {
  /** 0 — все категории. */
  category: number
  search: string
  filters: Filters
  sort: SortKey
}

export const EMPTY_CATALOG: CatalogState = {
  category: 0,
  search: '',
  filters: EMPTY_FILTERS,
  sort: 'pop',
}

/**
 * Отбор каталога живёт в адресе (`?cat=3&rx=otc&pmax=50000&sort=cheap`):
 * «назад» с товара, перезагрузка и присланная ссылка открывают тот же список.
 * Незнакомые значения молча отбрасываются — адрес мог набрать кто угодно.
 */
export function readCatalog(params: URLSearchParams): CatalogState {
  const rx = params.get('rx') as RxFilter | null
  const sort = params.get('sort') as SortKey | null
  return {
    category: Number(params.get('cat') ?? '') || 0,
    search: params.get('q') ?? '',
    filters: {
      rx: rx && RX_FILTERS.includes(rx) ? rx : 'all',
      stock: params.get('stock') === '1',
      pmin: digits(params.get('pmin')),
      pmax: digits(params.get('pmax')),
    },
    sort: sort && SORT_KEYS.includes(sort) ? sort : 'pop',
  }
}

/** Обратно в адрес; значения по умолчанию не пишутся, пустой отбор — чистый `/catalog`. */
export function writeCatalog(state: CatalogState): URLSearchParams {
  const params = new URLSearchParams()
  if (state.category) params.set('cat', String(state.category))
  if (state.search) params.set('q', state.search)
  if (state.filters.rx !== 'all') params.set('rx', state.filters.rx)
  if (state.filters.stock) params.set('stock', '1')
  if (state.filters.pmin) params.set('pmin', state.filters.pmin)
  if (state.filters.pmax) params.set('pmax', state.filters.pmax)
  if (state.sort !== 'pop') params.set('sort', state.sort)
  return params
}

function digits(value: string | null): string {
  return value && /^\d+$/.test(value) ? value : ''
}

/** Состояние экрана → параметры `GET /catalog/products`. */
export function toQuery(state: CatalogState): Query {
  return {
    q: state.search.trim(),
    categoryId: state.category || undefined,
    rx: state.filters.rx === 'all' ? '' : state.filters.rx,
    inStock: state.filters.stock ? true : undefined,
    priceMin: Number(state.filters.pmin) || undefined,
    priceMax: Number(state.filters.pmax) || undefined,
    sort: SORT_PARAM[state.sort],
  }
}

export interface FilterPill {
  key: keyof Filters
  label: string
  /** Значение, к которому возвращает крестик. */
  reset: Filters[keyof Filters]
}

/** Активные фильтры отдельными «таблетками» — их снимают по одному. */
export function activePills(
  filters: Filters,
  money: (value: number) => string,
  t: Translate,
): FilterPill[] {
  const pills: FilterPill[] = []
  if (filters.rx !== 'all') {
    pills.push({ key: 'rx', label: t(`catalog:rxFilter.${filters.rx}`), reset: 'all' })
  }
  if (filters.stock) pills.push({ key: 'stock', label: t('catalog:onlyInStock'), reset: false })
  if (filters.pmin) {
    const price = money(Number(filters.pmin))
    pills.push({ key: 'pmin', label: t('catalog:from', { price }), reset: '' })
  }
  if (filters.pmax) {
    const price = money(Number(filters.pmax))
    pills.push({ key: 'pmax', label: t('catalog:upTo', { price }), reset: '' })
  }
  return pills
}
